import React, { useState } from 'react';
import { FaEnvelope, FaUser, FaBuilding, FaPhone, FaGlobe, FaUsers, FaBullhorn, FaIndustry, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaSpinner, FaHandshake } from 'react-icons/fa';
import { submitAffiliateRequest } from '../api/client';
import SimpleCaptcha from './SimpleCaptcha';
import SEO from './SEO';

const AffiliateRequest = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    websiteUrl: '',
    socialMediaUrls: '',
    audienceSize: '',
    marketingChannels: [],
    niche: '',
    country: '',
    reason: '',
    additionalInfo: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);

  const audienceSizeOptions = [
    '1,000 - 10,000',
    '10,000 - 50,000',
    '50,000 - 100,000',
    '100,000 - 500,000',
    '500,000+'
  ];

  const marketingChannelOptions = [
    'Blog / Website',
    'YouTube',
    'Instagram',
    'TikTok',
    'Twitter / X',
    'Facebook',
    'Pinterest',
    'Email Newsletter',
    'Podcast',
    'Other'
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Please tell us why you want to become an affiliate';
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = 'Please provide at least 10 characters';
    } else if (formData.reason.length > 5000) {
      newErrors.reason = 'Must not exceed 5000 characters';
    }

    if (!captchaToken || !captchaAnswer) {
      newErrors.captcha = 'Please solve the security question';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCaptchaVerify = (token, answer) => {
    setCaptchaToken(token);
    setCaptchaAnswer(answer);
    if (answer) {
      setErrors(prev => prev.captcha ? { ...prev, captcha: '' } : prev);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleChannelChange = (channel) => {
    setFormData(prev => ({
      ...prev,
      marketingChannels: prev.marketingChannels.includes(channel)
        ? prev.marketingChannels.filter(c => c !== channel)
        : [...prev.marketingChannels, channel]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await submitAffiliateRequest(formData, captchaToken, captchaAnswer);
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        websiteUrl: '',
        socialMediaUrls: '',
        audienceSize: '',
        marketingChannels: [],
        niche: '',
        country: '',
        reason: '',
        additionalInfo: ''
      });
      setCaptchaToken('');
      setCaptchaAnswer('');
      setCaptchaKey(prev => prev + 1);
    } catch (error) {
      console.error('Error submitting affiliate request:', error);
      setSubmitStatus('error');
      setCaptchaKey(prev => prev + 1);
      setCaptchaToken('');
      setCaptchaAnswer('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = (fieldName) => `
    w-full px-4 py-3 pl-12 rounded-xl border-2
    ${errors[fieldName] ? 'border-red-300 focus:border-red-500' : 'border-neutral-200 focus:border-primary-400'}
    focus:outline-none focus:ring-2 focus:ring-primary-200
    bg-white transition-all duration-200
    text-neutral-700 placeholder-neutral-400
  `;

  const labelClasses = "block text-sm font-medium text-neutral-700 mb-2";

  return (
    <>
      <SEO
        title="Become an Affiliate | ItinerarySeek"
        description="Join the ItinerarySeek affiliate program and earn commissions by promoting our AI-powered travel planning platform."
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl shadow-lg mb-6">
              <FaHandshake className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-neutral-800 mb-4">
              Become an Affiliate Partner
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Join our affiliate program and earn commissions by sharing ItinerarySeek with your audience.
              Help travelers plan their perfect trips while growing your income.
            </p>
          </div>

          {/* Success Message */}
          {submitStatus === 'success' && (
            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-2xl flex items-start gap-4">
              <FaCheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800 mb-1">Application Submitted!</h3>
                <p className="text-green-700">
                  Thank you for your interest in becoming an affiliate partner. We'll review your application and get back to you soon.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {submitStatus === 'error' && (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4">
              <FaExclamationCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Submission Failed</h3>
                <p className="text-red-700">
                  Something went wrong. Please try again or contact us directly.
                </p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8 md:p-10">
            {/* Personal Information */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-neutral-800 mb-6 flex items-center gap-2">
                <FaUser className="text-primary-500" />
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className={labelClasses}>First Name *</label>
                  <div className="relative">
                    <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={inputClasses('firstName')}
                      placeholder="John"
                    />
                  </div>
                  {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
                </div>

                {/* Last Name */}
                <div>
                  <label className={labelClasses}>Last Name *</label>
                  <div className="relative">
                    <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={inputClasses('lastName')}
                      placeholder="Doe"
                    />
                  </div>
                  {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className={labelClasses}>Email Address *</label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={inputClasses('email')}
                      placeholder="john@example.com"
                    />
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className={labelClasses}>Phone Number</label>
                  <div className="relative">
                    <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={inputClasses('phone')}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-neutral-800 mb-6 flex items-center gap-2">
                <FaBuilding className="text-primary-500" />
                Business Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Name */}
                <div>
                  <label className={labelClasses}>Company/Brand Name</label>
                  <div className="relative">
                    <FaBuilding className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className={inputClasses('companyName')}
                      placeholder="Your Company Name"
                    />
                  </div>
                </div>

                {/* Website */}
                <div>
                  <label className={labelClasses}>Website URL</label>
                  <div className="relative">
                    <FaGlobe className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="url"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleInputChange}
                      className={inputClasses('websiteUrl')}
                      placeholder="https://yourwebsite.com"
                    />
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <label className={labelClasses}>Social Media URLs</label>
                  <div className="relative">
                    <FaBullhorn className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="text"
                      name="socialMediaUrls"
                      value={formData.socialMediaUrls}
                      onChange={handleInputChange}
                      className={inputClasses('socialMediaUrls')}
                      placeholder="Instagram, YouTube, TikTok links"
                    />
                  </div>
                </div>

                {/* Audience Size */}
                <div>
                  <label className={labelClasses}>Audience Size</label>
                  <div className="relative">
                    <FaUsers className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <select
                      name="audienceSize"
                      value={formData.audienceSize}
                      onChange={handleInputChange}
                      className={inputClasses('audienceSize')}
                    >
                      <option value="">Select audience size</option>
                      {audienceSizeOptions.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Marketing Channels */}
                <div className="md:col-span-2">
                  <label className={labelClasses}>Marketing Channels</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                    {marketingChannelOptions.map(channel => (
                      <label
                        key={channel}
                        className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-neutral-50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.marketingChannels.includes(channel)}
                          onChange={() => handleChannelChange(channel)}
                          className="w-4 h-4 text-primary-500 border-neutral-300 rounded focus:ring-primary-400"
                        />
                        <span className="text-sm text-neutral-700">{channel}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Niche */}
                <div>
                  <label className={labelClasses}>Niche/Industry</label>
                  <div className="relative">
                    <FaIndustry className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="text"
                      name="niche"
                      value={formData.niche}
                      onChange={handleInputChange}
                      className={inputClasses('niche')}
                      placeholder="Travel, Lifestyle, etc."
                    />
                  </div>
                </div>

                {/* Country */}
                <div className="md:col-span-2">
                  <label className={labelClasses}>Country</label>
                  <div className="relative">
                    <FaMapMarkerAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className={inputClasses('country')}
                      placeholder="United States"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Reason */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-neutral-800 mb-6 flex items-center gap-2">
                <FaHandshake className="text-primary-500" />
                Why Partner With Us?
              </h2>
              <div>
                <label className={labelClasses}>Why do you want to become an affiliate? *</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full px-4 py-3 rounded-xl border-2
                    ${errors.reason ? 'border-red-300 focus:border-red-500' : 'border-neutral-200 focus:border-primary-400'}
                    focus:outline-none focus:ring-2 focus:ring-primary-200
                    bg-white transition-all duration-200
                    text-neutral-700 placeholder-neutral-400 resize-none`}
                  placeholder="Tell us about your audience and why you'd be a great partner..."
                />
                {errors.reason && <p className="mt-1 text-sm text-red-500">{errors.reason}</p>}
              </div>

              <div className="mt-6">
                <label className={labelClasses}>Additional Information</label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 border-neutral-200 focus:border-primary-400
                    focus:outline-none focus:ring-2 focus:ring-primary-200
                    bg-white transition-all duration-200
                    text-neutral-700 placeholder-neutral-400 resize-none"
                  placeholder="Anything else you'd like us to know?"
                />
              </div>
            </div>

            {/* CAPTCHA */}
            <div className="mb-8">
              <SimpleCaptcha
                key={captchaKey}
                onVerify={handleCaptchaVerify}
              />
              {errors.captcha && <p className="mt-2 text-sm text-red-500">{errors.captcha}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white text-lg
                flex items-center justify-center gap-3 transition-all duration-200
                ${isSubmitting
                  ? 'bg-neutral-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 shadow-lg hover:shadow-xl'
                }`}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FaPaperPlane className="w-5 h-5" />
                  Submit Application
                </>
              )}
            </button>
          </form>

          {/* Benefits Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="font-semibold text-neutral-800 mb-2">Competitive Commissions</h3>
              <p className="text-neutral-600 text-sm">Earn generous commissions on every referral that converts.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="font-semibold text-neutral-800 mb-2">Real-Time Tracking</h3>
              <p className="text-neutral-600 text-sm">Monitor your performance with detailed analytics dashboard.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="font-semibold text-neutral-800 mb-2">Marketing Support</h3>
              <p className="text-neutral-600 text-sm">Access to banners, links, and promotional materials.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AffiliateRequest;
